import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AssessmentBackground } from '../../hd-ui-kit/src/components/ui/AssessmentBackground/AssessmentBackground';
import { AssessmentQuestionBackground } from '../../hd-ui-kit/src/components/ui/AssessmentQuestionBackground';
import { AssessmentNav } from '../../hd-ui-kit/src/components/ui/AssessmentNav';
import { Footer } from '../../hd-ui-kit/src/components/ui/Footer/Footer';
import { AssessmentStep } from '../../hd-ui-kit/src/components/screens/AssessmentStep';
import { AssessmentQuestionStacked } from '../../hd-ui-kit/src/components/ui/AssessmentQuestionStacked';
import { ErrorMessage } from '../../hd-ui-kit/src/components/ui/ErrorMessage';
import { StepTransition, type StepPhase } from '../../hd-ui-kit/src/components/ui/StepTransition';
import { AssessmentFreeResponse } from '../../hd-ui-kit/src/components/ui/AssessmentFreeResponse';
import { Button, ButtonArrow, ButtonSpinner } from '../../hd-ui-kit/src/components/ui/Button';
import { RatingDots } from '../../hd-ui-kit/src/components/ui/RatingDots';
import { InfoRow } from '../../hd-ui-kit/src/components/ui/InfoRow';

import '../../hd-ui-kit/src/styles/index.css';
import { heidrickTheme } from '../../hd-ui-kit/src/theme';
import { ThemeProvider } from '@mui/material/styles';
import Api from '../../Api';
import { globalContext } from '../../store';
import { useLocation, useNavigate } from 'react-router';
import { applyGlobalBranding as applyGlobalBrandingCommon } from '../../commons/commonFunctions';

// ── Types matching result.json API response shape ──────────────────────────

interface InstructionGroup { title: string; items: string[]; }

interface TemplatePage {
  pageKey: string;
  order: number;
  title: string;
  description?: string;
  content?: {
    callout?: string;
    instructionGroups?: InstructionGroup[];
    ratingScaleCode?: string;
    prompt?: string;
    instructions?: string;
    notObservedText?: string;
  };
  statementIds: string[];
  metadata?: {
    estimatedDurationMinutes?: { displayText: string; value: string };
    session?: { displayText: string; value: number };
  };
}

interface GlobalSection {
  type: 'Global'; order: number;
  header: { title: string; images?: { competencyId: string } };
  footer: { privacyPolicy: { text: string; url: string }; legalPolicy: { text: string; url: string }; needHelp: { text: string; url: string } };
  config: { allowSkip: boolean; allowBack: boolean; allowNext: boolean; allowSubmit: boolean; showProgressBar: boolean; renderMode: string };
}
interface IntroductionSection { type: 'Introduction'; order: number; title: string; pages: TemplatePage[]; }
interface QuestionSection {
  type: 'Question'; order: number; title: string; description?: string;
  ratingScaleCode: string;
  config?: { allowNotObserved?: boolean; notObservedLabel?: string };
  pages: TemplatePage[];
}
interface TextResponseSection { type: 'TextResponse'; order: number; title: string; config?: { isRequired?: boolean }; pages: TemplatePage[]; }
interface ConclusionSection { type: 'Conclusion'; order: number; title: string; pages: TemplatePage[]; }

type TemplateSection = GlobalSection | IntroductionSection | QuestionSection | TextResponseSection | ConclusionSection;

// ── Flat step model ────────────────────────────────────────────────────────

type StepKind = 'welcome' | 'instructions' | 'ratingScale' | 'question' | 'textResponse' | 'conclusion';

interface BaseStep { key: string; pageKey: string; kind: StepKind; title: string; description?: string; }
interface WelcomeStep extends BaseStep { kind: 'welcome'; metadata?: TemplatePage['metadata']; }
interface InstructionsStep extends BaseStep { kind: 'instructions'; groups: InstructionGroup[]; }
interface RatingScaleStep extends BaseStep { kind: 'ratingScale'; ratingScaleCode: string; notObservedText?: string; }
interface QuestionStep extends BaseStep {
  kind: 'question'; statementIds: string[]; ratingScaleCode: string;
  instructions?: string; notObservedText?: string; allowNotObserved: boolean; notObservedLabel: string;
}
interface TextResponseStep extends BaseStep { kind: 'textResponse'; prompt?: string; isRequired: boolean; statementIds: string[]; }
interface ConclusionStep extends BaseStep { kind: 'conclusion'; }

type FlatStep = WelcomeStep | InstructionsStep | RatingScaleStep | QuestionStep | TextResponseStep | ConclusionStep;
// ── Build flat step list from API sections ─────────────────────────────────

function buildSteps(sections: TemplateSection[]): FlatStep[] {
  const steps: FlatStep[] = [];
  let activeScaleCode = 'FREQ_7_MAYO_CLINIC';
  let activeNotObservedText: string | undefined;

  for (const section of [...sections].sort((a, b) => a.order - b.order)) {
    if (section.type === 'Global') continue;

    if (section.type === 'Introduction') {
      for (const page of [...section.pages].sort((a, b) => a.order - b.order)) {
        if (page.pageKey === 'welcome') {
          steps.push({ key: page.pageKey, pageKey: page.pageKey, kind: 'welcome', title: page.title, description: page.description, metadata: page.metadata });
        } else if (page.content?.instructionGroups) {
          steps.push({ key: page.pageKey, pageKey: page.pageKey, kind: 'instructions', title: page.title, description: page.description, groups: page.content.instructionGroups });
        } else {
          const code = page.content?.ratingScaleCode ?? activeScaleCode;
          activeScaleCode = code;
          activeNotObservedText = page.content?.notObservedText ?? activeNotObservedText;
          steps.push({ key: page.pageKey, pageKey: page.pageKey, kind: 'ratingScale', title: page.title, description: page.description, ratingScaleCode: code, notObservedText: page.content?.notObservedText });
        }
      }
    }

    if (section.type === 'Question') {
      activeScaleCode = section.ratingScaleCode;
      for (const page of [...section.pages].sort((a, b) => a.order - b.order)) {
        steps.push({
          key: page.pageKey, pageKey: page.pageKey, kind: 'question',
          title: page.title, description: page.description,
          statementIds: page.statementIds, ratingScaleCode: section.ratingScaleCode,
          instructions: page.content?.instructions,
          notObservedText: page.content?.notObservedText ?? (section.config?.allowNotObserved ? activeNotObservedText : undefined),
          allowNotObserved: section.config?.allowNotObserved ?? false,
          notObservedLabel: section.config?.notObservedLabel ?? 'Not Observed',
        });
      }
    }

    if (section.type === 'TextResponse') {
      for (const page of [...section.pages].sort((a, b) => a.order - b.order)) {
        steps.push({ key: page.pageKey, pageKey: page.pageKey, kind: 'textResponse', title: page.title, description: page.description, prompt: page.content?.prompt, isRequired: section.config?.isRequired ?? false, statementIds: page.statementIds ?? [] });
      }
    }

    if (section.type === 'Conclusion') {
      for (const page of [...section.pages].sort((a, b) => a.order - b.order)) {
        steps.push({ key: page.pageKey, pageKey: page.pageKey, kind: 'conclusion', title: page.title, description: page.description });
      }
    }
  }
  return steps;
}

// Contiguous question/textResponse runs that animate past each other via StepTransition.
// A new run starts when a non-question step interrupts or ratingScaleCode changes.
function buildRuns(steps: FlatStep[]): string[][] {
  const runs: string[][] = [];
  let run: string[] = [];
  let runScale: string | null = null;
  for (const step of steps) {
    if (step.kind === 'question' || step.kind === 'textResponse') {
      const scale: string | null = step.kind === 'question' ? step.ratingScaleCode : runScale;
      if (run.length > 0 && scale !== runScale) { runs.push(run); run = []; }
      run.push(step.key);
      runScale = scale;
    } else {
      if (run.length) { runs.push(run); run = []; }
      runScale = null;
    }
  }
  if (run.length) runs.push(run);
  return runs;
}

function buildProgressMap(steps: FlatStep[]): Record<string, number> {
  if (steps.length === 0) return {};
  if (steps.length === 1) return { [steps[0].key]: 100 };
  // first step → 1%, last step → 100%
  return Object.fromEntries(
    steps.map((s, i) => [s.key, Math.round(1 + (i / (steps.length - 1)) * 99)])
  );
}

// ── Shared UI primitives ───────────────────────────────────────────────────

const SecondaryOnDark = (props: React.ComponentProps<typeof Button>) => (
  <Button {...props} variant="secondary" sx={{ color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.85)', '&:hover,&:active,&[data-force-hover]': { border: '1.5px solid #FFFFFF' }, ...(props.sx as object) }} />
);

function StepActions({ isLight, onBack, backLabel = 'Back', label, onContinue, submitting }: {
  isLight?: boolean; onBack?: () => void; backLabel?: string; label: string; onContinue: () => void; submitting?: boolean;
}) {
  const Sec = isLight ? (p: React.ComponentProps<typeof Button>) => <Button variant="secondary" {...p} /> : SecondaryOnDark;
  return (
    <>
      {onBack && <Sec size="md" onClick={onBack} disabled={submitting}>{backLabel}</Sec>}
      <Button variant="primary" size="md" tone={isLight ? 'dark' : 'bright'} endIcon={submitting ? <ButtonSpinner /> : <ButtonArrow />} onClick={onContinue} disabled={submitting}>{label}</Button>
    </>
  );
}

function Counter({ n, total, isLight }: { n: number; total: number; isLight?: boolean }) {
  return (
    <span style={{ display: 'block', textAlign: 'center', fontFamily: 'var(--hd-font-family-avenir)', fontSize: 11, fontWeight: 300, lineHeight: 'normal', letterSpacing: '2px', textTransform: 'uppercase', color: isLight ? '#334255' : '#CCCCCC' }}>
      {n} of {total}
    </span>
  );
}

function MultiQuestionIntro({ description, instructions, notObservedText, scale, isLight }: { description: string; instructions: ReactNode; notObservedText?: string; scale: readonly string[]; isLight?: boolean }) {
  const muted = isLight ? '#334255' : '#CCCCCC';
  const accent = isLight ? '#00566A' : '#66FCFC';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24, width: '100%', textAlign: 'center' }}>
      <div style={{ width: '100%', fontFamily: 'var(--hd-font-family-avenir)', whiteSpace:'pre-wrap', fontSize: 22, fontWeight: 350, lineHeight: '30px', color: muted }}>{description}</div>
      <div aria-hidden="true" style={{ width: 40, height: 2, borderRadius: 1, background: accent }} />
      <p style={{whiteSpace:'pre-wrap', width: '100%', margin: 0, fontFamily: "var(--hd-font-family-avenir-lt-pro,'Avenir LT Pro')", fontSize: 19, fontWeight: 350, lineHeight: '26px', color: muted }}>
        <span style={{ color: accent }}>Instructions:</span> {instructions}
      </p>
      <RatingDots theme={isLight ? 'light' : 'dark'} items={scale} />
      {notObservedText && <p style={{ margin: 0, fontFamily: "var(--hd-font-family-avenir-lt-pro, 'Avenir LT Pro')", fontSize: 16, fontWeight: 350, lineHeight: '22px', color: isLight ? '#334255' : '#A9A9A9' }}>
        {notObservedText}
      </p>}
    </div>
  );
}

function HowToSections({ groups, isLight }: { groups: InstructionGroup[]; isLight?: boolean }) {
  const bodyStyle = { fontFamily: 'var(--hd-font-family-avenir)', fontSize: 19, fontWeight: 350, lineHeight: '28px', color: isLight ? '#334255' : '#A9A9A9' } as const;
  const headingColor = isLight ? '#00566A' : '#66FCFC';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {groups.map((g, i) => (
        <div key={g.title} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {i > 0 && <div aria-hidden style={{ width: 32, height: 1, background: isLight ? 'rgba(0,33,54,0.20)' : 'rgba(255,255,255,0.2)' }} />}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 22 }}>
            <h2 className='assessmentsh2' style={{ fontFamily: 'var(--hd-font-family-avenir)', color: headingColor }}>{g.title}</h2>
            {g.items.length === 1
              ? <p style={{ ...bodyStyle, margin: 0 }}>{g.items[0]}</p>
              : <ul style={{ ...bodyStyle, margin: 0, paddingLeft: 26, display: 'flex', flexDirection: 'column', gap: 4 }}>{g.items.map(item => <li key={item}>{item}</li>)}</ul>
            }
          </div>
        </div>
      ))}
    </div>
  );
}

function CheckIcon({ isLight }: { isLight?: boolean }) {
  const ring = isLight ? 'rgba(0,86,106,0.3)' : 'rgba(102,252,252,0.3)';
  const check = isLight ? '#00566A' : '#66FCFC';
  return (
    <div style={{ width: 80, height: 80, borderRadius: '50%', border: `1px solid ${ring}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-hidden="true">
      <svg width="32" height="24" viewBox="0 0 32 24" fill="none">
        <path d="M2.5 12.5 L12 21.5 L29.5 2.5" stroke={check} strokeWidth="3.281" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

// Fallback: find the next step index from saved scores when stepsCompleted is unavailable
function deriveStepCountFromScores(builtSteps: FlatStep[], scores: any[]): number {
  let maxIdx = -1;
  for (let i = 0; i < builtSteps.length; i++) {
    const s = builtSteps[i];
    if (s.kind === 'question' && s.statementIds.length > 0) {
      if (s.statementIds.every(sid => scores.some((sc: any) => sc.StatementID === sid))) maxIdx = i;
    } else if (s.kind === 'textResponse') {
      const sid = s.statementIds[0] ?? '';
      const hit = scores.some((sc: any) => sc.QuestionType === 'Additional Info' && (sid ? sc.StatementID === sid : sc.QuestionText === s.description));
      if (hit) maxIdx = i;
    }
  }
  return maxIdx + 1;
}

// ── Component ──────────────────────────────────────────────────────────────

export default function MayoSelf({ theme = 'light' }: { theme?: 'light' | 'dark' } = {}) {
  const { globalState, dispatch } = useContext(globalContext);
  const isLight = theme === 'light';
  const navigate = useNavigate();
  const location = useLocation();
  const receivedData = location.state;
  if(receivedData === null) navigate('/dashboard'); // replace with your default page
  const assessmentId = receivedData?.raterDetails?.assessmentId;
  const templateId = receivedData?.raterDetails?.surveyTemplate;
  const [headerLogo, setHeaderLogo] = useState<string>('');
  const [, setHeaderImage] = useState<string>('');
  const [, setPrimaryColor] = useState<string>('#BA3A19');
  const [stepKey, setStepKey] = useState('');
  const [ratings, setRatings] = useState<Record<string, (number | undefined)[]>>({});
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [navGlass, setNavGlass] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [cardTier, setCardTier] = useState<'md' | 'tablet' | 'compact' | 'mobile'>('md');
  const [settledRun, setSettledRun] = useState<number | null>(null);
  // true until template + statement + scale API calls complete
  const [loading, setLoading] = useState(true);
  const [statementMap, setStatementMap] = useState<Record<string, string>>({});
  const [statementDescMap, setStatementDescMap] = useState<Record<string, string>>({});
  const [ratingScaleMap, setRatingScaleMap] = useState<Record<string, { score: number; text: string }[]>>({});
  // blocks render until scale fetch has settled (success or fail)
  const [scalesReady, setScalesReady] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [assessmentData, setAssessmentData] = useState<any>(null);
  const [accumulatedScores, setAccumulatedScores] = useState<any[]>([]);
  // true after fetchAssessmentQuestionsAnswered settles so navigation waits for restored step count
  const [restoreReady, setRestoreReady] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [submitting, setSubmitting] = useState(false);
  // tracks "Not Observed" checkbox state per step key and statement index
  const [notObservedMap, setNotObservedMap] = useState<Record<string, boolean[]>>({});

  // keyed by ratingScaleCode (e.g. "RS12") and ratingScaleName (e.g. "Frequency_7_Mayo")
  const getScaleLabels = (code: string): readonly string[] => ratingScaleMap[code]?.map(o => o.text) ?? [];

  const handleNotObservedChange = (stepKey: string, idx: number, checked: boolean) => {
    setNotObservedMap(prev => {
      const next = [...(prev[stepKey] ?? [])];
      next[idx] = checked;
      return { ...prev, [stepKey]: next };
    });
    // clear the rating for this row when marking not-observed
    if (checked) {
      setRatings(prev => {
        const next = [...(prev[stepKey] ?? [])];
        next[idx] = undefined;
        return { ...prev, [stepKey]: next };
      });
    }
  };

  // sections come from global state after the API fetch below
  const sections = useMemo<TemplateSection[]>(() => globalState.inHouseAssessmentData ?? [], [globalState.inHouseAssessmentData]);

  // Extract footer links from Global section (Privacy, Legal, Need Help)
  const globalSection = useMemo(() => sections?.find((s): s is GlobalSection => s.type === 'Global'), [sections]);
  const footerLinks = useMemo(() => {
    if (!globalSection?.footer) return undefined;
    const footerData = globalSection.footer;
    return [
      { label: footerData.privacyPolicy?.text ?? 'Privacy Notice', href: footerData.privacyPolicy?.url, target: '_blank' },
      { label: footerData.legalPolicy?.text ?? 'Legal Notice', href: footerData.legalPolicy?.url, target: '_blank' },
      { label: footerData.needHelp?.text ?? 'Need Help?', href: footerData.needHelp?.url, target: '_blank' },
    ];
  }, [globalSection]);

  const steps   = useMemo(() => buildSteps(sections), [sections]);
  const runs    = useMemo(() => buildRuns(steps), [steps]);
  const progMap = useMemo(() => buildProgressMap(steps), [steps]);

  const assessmentNameText = useMemo(() => {
    //const g = sections.find((s): s is GlobalSection => s.type === 'Global');

    if(receivedData?.raterDetails?.isSelfAssessment) {
      return <span style={{color: '#002136', fontSize: '22px', fontWeight: 'bolder'}}>{receivedData?.raterDetails?.surveyNameLeaders || '360 Survey'}</span>;
    } else {
      return `${receivedData?.raterDetails?.surveyNameRaters ?? ''} ${receivedData?.raterDetails?.leaderName ?? ''}`.trim() || '360 Survey';
    }
    
  }, [receivedData]);
  const assessmentName = useMemo(() => {
    if (receivedData?.raterDetails?.isSelfAssessment) return assessmentNameText;

    return (
      <>
        <span style={{color: '#002136', fontSize: '22px', fontWeight: 'bolder'}}>{receivedData?.raterDetails?.surveyNameRaters ?? ''}</span>{' '}
        <span style={{color: '#18768c', fontSize: '22px', fontWeight: 'bolder'}}>{receivedData?.raterDetails?.leaderName ?? ''}</span>
      </>
    );
  }, [assessmentNameText, receivedData]);

  const questionKeys = useMemo(() => steps.filter(s => s.kind === 'question' || s.kind === 'textResponse').map(s => s.key), [steps]);

  // Fetch template, statements, and rating scales; loader stays on until all settle
  useEffect(() => {
    applyGlobalBrandingCommon(globalState, setHeaderLogo, setHeaderImage, setPrimaryColor, dispatch);
    Api.fetchMayoTemplateDetails(templateId)
      .then(async (templates: any) => {
        if (!templates?.success) return;
        const templateData = templates?.response?.data?.data;
        const sortedSections = [...(templateData?.sections ?? [])].sort((a: any, b: any) => a.order - b.order);
        dispatch({ type: 'SET_IN_HOUSE_ASSESSMENT_DATA', payload: sortedSections });

        const clientId = globalState.clientData?.id;

        // collect every statementId across all pages in all sections, deduplicated
        const statementCodeList = [
          ...new Set(
            sortedSections
              .flatMap((s: any) => s.pages ?? [])
              .flatMap((p: any) => p.statementIds ?? [])
              .filter(Boolean)
          ),
        ].join(',');

        if (statementCodeList) {
          const statementsResult = await Api.getStatementData({ clientId, statementCodeList });
          if (statementsResult?.success) {
            const items: any[] = statementsResult?.response?.data?.data ?? [];
            const map: Record<string, string> = {};
            const descMap: Record<string, string> = {};
            items.forEach((item: any) => { map[item.statementCode] = item.name; descMap[item.statementCode] = item.description ?? ''; });
            setStatementMap(map);
            setStatementDescMap(descMap);
          }
        }

        // mirror buildSteps' activeScaleCode propagation to capture every code that will be rendered
        let activeCode = '';
        const scaleCodes = new Set<string>();
        for (const section of sortedSections) {
          if (section.type === 'Introduction') {
            for (const page of [...(section.pages ?? [])].sort((a: any, b: any) => a.order - b.order)) {
              if (page.pageKey !== 'welcome' && !page.content?.instructionGroups) {
                const code: string = page.content?.ratingScaleCode ?? activeCode;
                if (code) { activeCode = code; scaleCodes.add(code); }
              }
            }
          }
          if (section.type === 'Question' && section.ratingScaleCode) {
            activeCode = section.ratingScaleCode;
            scaleCodes.add(section.ratingScaleCode);
          }
        }
        const ratingScaleCodeList = [...scaleCodes].join(',');

        if (ratingScaleCodeList) {
          const scaleResult = await Api.getRatingScaleData({ clientId, RSCodeList: ratingScaleCodeList });
          if (scaleResult?.success) {
            const responseDict: Record<string, any[]> = Object.assign({}, ...(scaleResult?.response?.data?.data ?? []));
            const map: Record<string, { score: number; text: string }[]> = {};
            Object.entries(responseDict).forEach(([code, options]) => {
              const mapped = (options as any[]).map((o: any) => ({ score: o.score, text: o.text }));
              map[code] = mapped;
              const name: string | undefined = (options as any[])[0]?.ratingScaleName;
              if (name) map[name] = mapped;
            });
            setRatingScaleMap(map);
          }
        }
        setScalesReady(true);

        // fetch previously saved answers and restore session state
        const answeredResult = await Api.fetchAssessmentQuestionsAnswered(assessmentId);
        if (answeredResult?.success) {
          const body = answeredResult?.response?.data;
          const assessmentItem = body?.data?.[0] ?? body?.[0];
          setAssessmentData(assessmentItem);
          if (assessmentItem?.results) {
            const { StatementScores, stepsCompleted, AppraiseStartDate, AppraiseStartedAt, AppraiseCompletedOn } = assessmentItem.results;
            if (AppraiseCompletedOn) isAssessmentCompletedRef.current = true;
            if (Array.isArray(StatementScores) && StatementScores.length > 0) {
              setAccumulatedScores(StatementScores);
            }
            appraiseStartDateRef.current = (AppraiseStartDate != null && AppraiseStartDate !== '') ? AppraiseStartDate : new Date().toISOString();
            appraiseStartedAtRef.current = (AppraiseStartedAt != null && AppraiseStartedAt !== '') ? AppraiseStartedAt : new Date().toISOString();
            // MongoDB stores numbers as { $numberInt: "17" } — parse either format
            const completed = stepsCompleted?.completed?.$numberInt
              ? Number(stepsCompleted.completed.$numberInt)
              : Number(stepsCompleted?.completed ?? 0);
            if (completed > 0) completedStepCountRef.current = completed;
          }
        }
        setRestoreReady(true);
      })
      .catch((error: any) => {
        console.error('Error fetching Mayo templates:', error);
        setScalesReady(true);
        setRestoreReady(true);
      })
      .finally(() => {
        setLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigate to the step after the last completed one — waits for restore data to settle
  useEffect(() => {
    if (steps.length > 0 && restoreReady && !stepKey) {
      if (isAssessmentCompletedRef.current) {
        navigate('/dashboard');
        return;
      }
      // use the higher of stepsCompleted vs. score-derived count so text responses are never missed
      const fromStepsCompleted = completedStepCountRef.current;
      const fromScores = deriveStepCountFromScores(steps, accumulatedScores);
      const targetIdx = Math.min(Math.max(fromStepsCompleted, fromScores), steps.length - 1);
      setStepKey(steps[targetIdx].key);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, restoreReady]);

  // Seed ratings/texts from a restored session so going back shows previous answers
  useEffect(() => {
    if (hasSeededRatingsRef.current || !restoreReady || !accumulatedScores.length || !steps.length) return;
    hasSeededRatingsRef.current = true;
    const newRatings: Record<string, (number | undefined)[]> = {};
    const newTexts: Record<string, string> = {};
    const newNotObserved: Record<string, boolean[]> = {};
    for (const step of steps) {
      if (step.kind === 'question') {
        const mapped = step.statementIds.map((sid: string) => {
          const score = accumulatedScores.find((s: any) => s.StatementID === sid);
          return score?.RawScore ? Number(score.RawScore) : undefined;
        });
        if (mapped.some((v: number | undefined) => v !== undefined)) newRatings[step.key] = mapped;
        const notObs = step.statementIds.map((sid: string) => {
          const score = accumulatedScores.find((s: any) => s.StatementID === sid);
          return score?.NotObserved === true;
        });
        if (notObs.some(v => v)) newNotObserved[step.key] = notObs;
      }
      if (step.kind === 'textResponse') {
        const sid = step.statementIds[0] ?? '';
        const match = accumulatedScores.find((s: any) =>
          s.QuestionType === 'Additional Info' &&
          (s.QuestionText === step.prompt || s.QuestionText === step.title || s.QuestionText === step.description || (sid && s.StatementID === sid))
        );
        if (match?.Answer) newTexts[step.key] = match.Answer;
      }
    }
    if (Object.keys(newRatings).length > 0) setRatings(newRatings);
    if (Object.keys(newTexts).length > 0) setTexts(newTexts);
    if (Object.keys(newNotObserved).length > 0) setNotObservedMap(newNotObserved);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreReady, accumulatedScores, steps]);

  // Responsive card tier
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mqMobile  = window.matchMedia('(max-width: 499px)');
    const mqCompact = window.matchMedia('(min-width: 500px) and (max-width: 575px)');
    const mqTablet  = window.matchMedia('(min-width: 576px) and (max-width: 991px)');
    const mqs = [mqMobile, mqCompact, mqTablet];
    const update = () => setCardTier(mqMobile.matches ? 'mobile' : mqCompact.matches ? 'compact' : mqTablet.matches ? 'tablet' : 'md');
    update();
    mqs.forEach(mq => mq.addEventListener('change', update));
    return () => mqs.forEach(mq => mq.removeEventListener('change', update));
  }, []);

  const currentIdx  = steps.findIndex(s => s.key === stepKey);
  const currentStep = steps[currentIdx] ?? null;
  const prevStep    = currentIdx > 0 ? steps[currentIdx - 1] : null;
  const nextStep    = currentIdx < steps.length - 1 ? steps[currentIdx + 1] : null;
  const percent     = progMap[stepKey] ?? 0;
  const isQuestion  = currentStep?.kind === 'question' || currentStep?.kind === 'textResponse';
  const runIdx      = runs.findIndex(r => r.includes(stepKey));

  const go = (key: string) => { setShowErrors(false); setStepKey(key); };

  // Settled-run animation gate
  useEffect(() => {
    if (runIdx === -1) { setSettledRun(null); return; }
    const t = window.setTimeout(() => setSettledRun(runIdx), 1250);
    return () => window.clearTimeout(t);
  }, [runIdx]);

  // Scroll reset
  const shellRef = useRef<HTMLDivElement>(null);
  const prevKeyRef = useRef(stepKey);
  const startedAtRef = useRef<string | null>(null);
  // stores the completed step index from a prior session to redirect on load
  const completedStepCountRef = useRef<number>(0);
  // preserves initial AppraiseStartDate/AppraiseStartedAt so they are never overwritten once set
  const appraiseStartDateRef = useRef<string | null>(null);
  const appraiseStartedAtRef = useRef<string | null>(null);
  // prevents ratings/texts seed from running more than once
  const hasSeededRatingsRef = useRef(false);
  // true when AppraiseCompletedOn is set — assessment was fully submitted
  const isAssessmentCompletedRef = useRef(false);
  useLayoutEffect(() => {
    const sc = shellRef.current;
    if (!sc) return;
    const prev = prevKeyRef.current;
    prevKeyRef.current = stepKey;
    const withinRun = prev !== stepKey && runs.some(r => r.includes(stepKey) && r.includes(prev));
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (withinRun && !reduced) {
      const t = window.setTimeout(() => { sc.scrollTop = 0; }, 450);
      return () => window.clearTimeout(t);
    }
    sc.scrollTop = 0;
    const raf = requestAnimationFrame(() => { sc.scrollTop = 0; });
    return () => cancelAnimationFrame(raf);
  }, [stepKey, runs]);

  // Frosted nav
  useLayoutEffect(() => {
    const sc = shellRef.current;
    if (!sc) return;
    const onScroll = () => setNavGlass(sc.scrollTop > 2);
    onScroll();
    sc.addEventListener('scroll', onScroll, { passive: true });
    return () => sc.removeEventListener('scroll', onScroll);
  }, [stepKey, loading]);

  const baseProps = { brand: headerLogo, assessmentName, percent, chrome: false as const, theme };

  const submitStepAnswers = async (
    statementScores: any[],
    completedOn: string | null = null,
    stepsCompleted: { total: number; completed: number } = { total: 0, completed: 0 }
  ) => {
    if (!assessmentId) return;
    setSubmitting(true);
    try {
      await Api.updateAssessmentsStatus({
        results: {
          AppraiseID: receivedData?.raterDetails?.assessmentStructureId, //assessment structure ID
          AppraiseReference: assessmentNameText,
          AppraiseTitle: assessmentNameText,
          AppraiseStartDate: appraiseStartDateRef.current ?? startedAtRef.current,
          AppraiseEndDate: completedOn,
          AppraiseStartedAt: appraiseStartedAtRef.current ?? startedAtRef.current,
          AppraiseCompletedOn: completedOn,
          StatementScores: statementScores,
          stepsCompleted,
          TieBreaks: [],
        },
      }, assessmentId);
    } finally {
      setSubmitting(false);
    }
  };

  const saveAndCloseAssessment = async (statementScores: any[]) => {
    const payload = {
      identifier: 'in-house-appraisal',
      notificationType: 'AssessmentCompleted',
      assessmentId: assessmentId,
      applicationID: null,
      results: {
        AppraiseID: receivedData?.raterDetails?.assessmentStructureId, //assessment structure ID
        AppraiseReference: assessmentNameText,
        AppraiseTitle: assessmentNameText,
        AppraiseStartDate: appraiseStartDateRef.current ?? startedAtRef.current,
        AppraiseEndDate: new Date().toISOString(),
        AppraiseStartedAt: appraiseStartedAtRef.current ?? startedAtRef.current,
        AppraiseCompletedOn: new Date().toISOString(),
        StatementScores: statementScores,
        TieBreaks: [],
      },
    };
    setSubmitting(true);
    try {
      await Api.saveAndCompleteAssessment(payload).then((response: any) => {
        if (response?.success) {
          navigate('/dashboard');
        } else {
          console.error('Failed to save and close assessment:', response);
        }
      });
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render one question or text-response step inside StepTransition ──────
  const renderQuestionStep = (key: string, phase: StepPhase, direction: 1 | -1): ReactNode => {
    const step = steps.find(s => s.key === key);
    if (!step) return null;
    const locked = phase === 'exit';
    const sIdx  = steps.findIndex(s => s.key === key);
    const sPrev = sIdx > 0 ? steps[sIdx - 1] : null;
    const sNext = sIdx < steps.length - 1 ? steps[sIdx + 1] : null;
    const qNum  = questionKeys.indexOf(key) + 1;

    if (step.kind === 'question') {
      const scale              = getScaleLabels(step.ratingScaleCode);
      const answers            = ratings[key] ?? [];
      const notObservedForStep = notObservedMap[key] ?? [];
      const allDone            = step.statementIds.every((_, i) => answers[i] !== undefined || notObservedForStep[i]);
      const tryNext  = async () => {
        if (locked) return;
        if (!allDone) { setShowErrors(true); return; }
        const currentScores = step.statementIds.map((sid, i) => ({
          StatementID: sid,
          StatementCode: sid,
          LowLevelCriteriaName: step.title,
          MidLevelCriteriaName: step.title,
          HighLevelCriteriaName: step.title,
          RawScore: notObservedForStep[i] ? null : (answers[i] !== undefined ? String(answers[i]) : ''),
          Score: notObservedForStep[i] ? null : (answers[i] !== undefined ? String(answers[i]) : ''),
          NotObserved: notObservedForStep[i] ?? false,
          QuestionText: statementMap[sid] ?? sid,
          StatementText: statementDescMap[sid] ?? '',
        }));
        const updatedScores = [
          ...accumulatedScores.filter(s => !step.statementIds.includes(s.StatementID)),
          ...currentScores,
        ];
        setAccumulatedScores(updatedScores);
        await submitStepAnswers(updatedScores, null, { total: steps.length, completed: sIdx + 1 });
        if (sNext) go(sNext.key);
      };
      return (
        <AssessmentStep {...baseProps} template="question" phase={phase} direction={direction}
          eyebrow={<Counter n={qNum} total={questionKeys.length} isLight={isLight} />}
          title={step.title}
          body={<MultiQuestionIntro isLight={isLight} description={step.description ?? ''} instructions={step.instructions ?? 'Read each statement and rate how frequently the behavior is demonstrated.'} notObservedText={step.notObservedText} scale={scale} />}
          actions={<StepActions isLight={isLight} onBack={sPrev ? () => { if (!locked) go(sPrev.key); } : undefined} backLabel="Previous" label="Continue" onContinue={tryNext} submitting={submitting} />}
          belowActions={
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, paddingTop: 16, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
              {showErrors && !allDone && <ErrorMessage theme={isLight ? 'light' : 'dark'}>Please respond to every statement before continuing.</ErrorMessage>}
            </div>
          }
        >
          <div role="group" aria-label={step.title} style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%' }}>
            {step.statementIds.map((sid, i) => {
              const rowError = showErrors && answers[i] === undefined && !notObservedForStep[i];
              return (
                <div key={sid} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                  <AssessmentQuestionStacked
                    statement={statementMap[sid] ?? sid}
                    count={scale.length}
                    endpointStart={scale[0]}
                    endpointEnd={scale[scale.length - 1]}
                    valueLabels={[...scale]}
                    value={answers[i]}
                    onChange={(v) => setRatings(prev => { const next = [...(prev[key] ?? Array(step.statementIds.length).fill(undefined))]; next[i] = v; return { ...prev, [key]: next }; })}
                    notObserved={step.allowNotObserved}
                    notObservedChecked={notObservedForStep[i] ?? false}
                    onNotObservedChange={(c) => handleNotObservedChange(key, i, c)}
                    notObservedLabel={step.notObservedLabel}
                    error={rowError}
                    forceLayout={cardTier}
                    theme={theme}
                    width="100%"
                  />
                  {rowError && <ErrorMessage theme={isLight ? 'light' : 'dark'}>Please select a rating.</ErrorMessage>}
                </div>
              );
            })}
          </div>
        </AssessmentStep>
      );
    }

    if (step.kind === 'textResponse') {
      const text   = texts[key] ?? '';
      const empty  = text.trim() === '';
      const tryNext = async () => {
        if (locked) return;
        if (empty) { setShowErrors(true); return; }
        const currentScore = {
          QuestionType: 'Additional Info',
          QuestionText: step.description,
          StatementID: step.statementIds[0] ?? '',
          StatementCode: step.statementIds[0] ?? '',
          StatementText: step.description,
          Answer: text || null,
        };
        const sid = step.statementIds[0] ?? '';
        const updatedScores = [
          ...accumulatedScores.filter(s => {
            if (s.QuestionType !== 'Additional Info') return true;
            return sid ? s.StatementID !== sid : s.QuestionText !== step.description;
          }),
          currentScore,
        ];
        setAccumulatedScores(updatedScores);
        await submitStepAnswers(updatedScores, null, { total: steps.length, completed: sIdx + 1 });
        if (sNext) go(sNext.key);
      };
      return (
        <AssessmentStep {...baseProps} template="question" phase={phase} direction={direction}
          eyebrow={<Counter n={qNum} total={questionKeys.length} isLight={isLight} />}
          title={step.title}
          body={step.description}
          actions={<StepActions isLight={isLight} onBack={sPrev ? () => { if (!locked) go(sPrev.key); } : undefined} backLabel="Previous" label="Continue" onContinue={tryNext} submitting={submitting} />}
          belowActions={
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, paddingTop: 16, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
              {showErrors && empty && <ErrorMessage theme={isLight ? 'light' : 'dark'}>Please enter a response before continuing.</ErrorMessage>}
            </div>
          }
        >
          <div style={{ width: '100%', maxWidth: 535, margin: '0 auto' }}>
            <AssessmentFreeResponse value={text} onChange={(v) => setTexts(p => ({ ...p, [key]: v }))} maxLength={800} theme={theme} aria-label={step.title} />
          </div>
        </AssessmentStep>
      );
    }

    return null;
  };

  // ── Render the current step ──────────────────────────────────────────────
  const renderStep = (): ReactNode => {
    if (!currentStep) return null;
    // Question/textResponse pages animate within their run
    const run = runs.find(r => r.includes(stepKey));
    if (run) {
      return (
        <StepTransition index={run.indexOf(stepKey)} durationMs={1250} style={{ flex: '1 1 auto', minHeight: 'auto', gridTemplateRows: 'minmax(min-content, 1fr)' }}>
          {({ index, phase, direction }) => renderQuestionStep(run[index], phase === 'rest' && settledRun !== runIdx ? 'enter' : phase, direction)}
        </StepTransition>
      );
    }

    if (currentStep.kind === 'welcome') {
      const items: { number: string; label: string }[] = [];
      if (currentStep.metadata?.estimatedDurationMinutes) items.push({ number: currentStep.metadata.estimatedDurationMinutes.value, label: currentStep.metadata.estimatedDurationMinutes.displayText });
      if (currentStep.metadata?.session) items.push({ number: String(currentStep.metadata.session.value), label: currentStep.metadata.session.displayText });
      return (
        <AssessmentStep {...baseProps} eyebrow="Introduction" title={currentStep.title} body={<span style={{ whiteSpace: 'pre-wrap' }}>{currentStep.description}</span>}
          actions={<StepActions isLight={isLight} label="Begin" onContinue={async () => { if (!startedAtRef.current) startedAtRef.current = new Date().toISOString(); await submitStepAnswers(accumulatedScores, null, { total: steps.length, completed: currentIdx + 1 }); nextStep && go(nextStep.key); }} submitting={submitting} />}
        >
          {items.length > 0 && <InfoRow theme={theme} items={items} />}
        </AssessmentStep>
      );
    }

    if (currentStep.kind === 'instructions') {
      return (
        <AssessmentStep {...baseProps} eyebrow="Instructions" title={currentStep.title}
          actions={<StepActions isLight={isLight} onBack={prevStep ? () => go(prevStep.key) : undefined} label="Continue" onContinue={async () => { await submitStepAnswers(accumulatedScores, null, { total: steps.length, completed: currentIdx + 1 }); nextStep && go(nextStep.key); }} submitting={submitting} />}
        >
          <HowToSections groups={currentStep.groups} isLight={isLight} />
        </AssessmentStep>
      );
    }

    if (currentStep.kind === 'ratingScale') {
      return (
        <AssessmentStep {...baseProps} eyebrow="Instructions" title={currentStep.title} body={<span style={{ whiteSpace: 'pre-wrap' }}>{currentStep.description}</span>}
          actions={<StepActions isLight={isLight} onBack={prevStep ? () => go(prevStep.key) : undefined} backLabel="Previous" label="Continue" onContinue={async () => { await submitStepAnswers(accumulatedScores, null, { total: steps.length, completed: currentIdx + 1 }); nextStep && go(nextStep.key); }} submitting={submitting} />}
        >
          <RatingDots theme={theme} items={getScaleLabels(currentStep.ratingScaleCode)} />
          {currentStep.notObservedText && <p style={{ margin: 0, fontFamily: "var(--hd-font-family-avenir-lt-pro, 'Avenir LT Pro')", fontSize: 16, fontWeight: 350, lineHeight: '22px', color: isLight ? '#334255' : '#A9A9A9' }}>
            {currentStep.notObservedText}
          </p>}
        </AssessmentStep>
      );
    }

    if (currentStep.kind === 'conclusion') {
      return (
        <AssessmentStep {...baseProps} preTitleIcon={<CheckIcon isLight={isLight} />} title={currentStep.title} body={<span style={{ whiteSpace: 'pre-wrap' }}>{currentStep.description}</span>}
          actions={<StepActions isLight={isLight} onBack={prevStep ? () => go(prevStep.key) : undefined} label="Save and close" 
          onContinue={async () => { 
            await saveAndCloseAssessment(accumulatedScores); 
          }} submitting={submitting} 
        />}
        />
      );
    }

    return null;
  };

  // Loading state while any API call is still pending
  if (loading || sections.length === 0 || !scalesReady) {
    return (
      <ThemeProvider theme={heidrickTheme}>
        <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isLight ? 'var(--hd-color-assessment-bg-light)' : 'var(--hd-color-assessment-bg)' }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', border: `3px solid ${isLight ? 'rgba(0,86,106,0.15)' : 'rgba(102,252,252,0.15)'}`, borderTopColor: isLight ? '#00566A' : '#66FCFC', animation: 'hd-spin 0.9s linear infinite' }} />
          <style>{`@keyframes hd-spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={heidrickTheme}>
      <div style={{ position: 'fixed', inset: 0, background: isLight ? 'var(--hd-color-assessment-bg-light)' : 'var(--hd-color-assessment-bg)', color: isLight ? 'var(--hd-text-hero-h1-color-light)' : 'var(--hd-text-hero-h1-color)', isolation: 'isolate', overflow: 'hidden' }}>
        {isQuestion ? <AssessmentQuestionBackground theme={theme} /> : <AssessmentBackground theme={theme} />}

        <div ref={shellRef} style={{ position: 'absolute', inset: 0, zIndex: 1, overflowY: 'auto', overflowAnchor: 'none' }}>
          <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <AssessmentNav brand={`hs`} headerLogo={headerLogo} assessmentName={assessmentName} assessmentNameText={assessmentNameText} percent={percent} scrolled={navGlass} theme={theme} />
            </div>

            {/* Keyed by run index within a run, by stepKey outside — keeps StepTransition alive across question pages */}
            <div
              key={runIdx !== -1 ? `run-${runIdx}` : stepKey}
              style={{ flex: '1 1 auto', display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gridTemplateRows: 'minmax(min-content,1fr)' }}
            >
              {renderStep()}
            </div>

            <div style={{ marginTop: 40 }}>
              <Footer variant={isLight ? 'assessment-light' : 'assessment'} links={footerLinks} />
            </div>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
}
